import { Alert, Box, CircularProgress } from '@mui/material';
import React from 'react';
import { getPasswordChecks } from '../common/PasswordStrengthMeter';
import { KitButton } from '../common/MobileKit';
import { KitAvatar, KitField, KitFormBar, KitInput, KitInputButton, KitInputIco, KitProgress } from '../common/MobileKitExtras';
import { protoIcon } from '../common/ProtoIcon';
import { MobileAuthPage } from './MobileAuth';

/**
 * Alterar senha no mobile — `prototipos-mobile/auth/alterar-senha.html`.
 * Só apresentação; o formik/PUT ficam em `pages/ChangePassword.js`.
 */
const Lock = protoIcon('lock');
const Eye = protoIcon('eye');
const EyeOff = protoIcon('eyeoff');

const HINTS = { length: 'use 8 ou mais caracteres', upper: 'adicione letras maiúsculas', number: 'adicione números', special: 'adicione símbolos' };

function strength(password) {
    const checks = getPasswordChecks(password);
    const score = Object.values(checks).filter(Boolean).length;
    const labels = ['', 'Muito fraca', 'Média', 'Forte', 'Muito forte'];
    const tones = [undefined, 'red', 'amber', undefined, 'green'];
    const missing = Object.keys(HINTS).find((k) => !checks[k]);
    return { score, label: labels[score], tone: tones[score], hint: missing ? HINTS[missing] : '' };
}

export default function MobileChangePassword({ formik, isLoading, successMessage, errorMessage, showNewPassword, setShowNewPassword, showConfirmPassword, setShowConfirmPassword, onBack }) {
    const s = strength(formik.values.newPassword);
    const newErr = formik.touched.newPassword && formik.errors.newPassword;
    const confirmMatches = formik.values.confirmPassword.length > 0 && !formik.errors.confirmPassword;
    const confirmErr = !confirmMatches && formik.touched.confirmPassword && formik.errors.confirmPassword;
    const eye = (shown, set) => (
        <KitInputButton aria-label="alternar visibilidade da senha" onClick={() => set(!shown)}>
            {shown ? <EyeOff size={20} /> : <Eye size={20} />}
        </KitInputButton>
    );

    return (
        <MobileAuthPage title="Alterar senha" onBack={onBack}>
            <Box sx={{ textAlign: 'center', mb: '16px', backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '16px', p: '16px', boxShadow: '0 1px 2px rgba(15,23,42,.06)' }}>
                <KitAvatar tone="soft" size={76} sx={{ m: '0 auto 10px' }} text={<Lock size={32} />} />
                <Box sx={{ fontWeight: 700 }}>Crie uma nova senha</Box>
                <Box sx={{ fontSize: 12.5, color: 'text.disabled' }}>Escolha uma senha forte que você não use em outros lugares.</Box>
            </Box>

            {successMessage && <Alert severity="success" sx={{ mb: '14px', borderRadius: '14px' }}>{successMessage}</Alert>}
            {errorMessage && <Alert severity="error" sx={{ mb: '14px', borderRadius: '14px' }}>{typeof errorMessage === 'string' ? errorMessage : 'Não foi possível alterar a senha.'}</Alert>}

            <Box component="form" id="mobile-change-password" onSubmit={formik.handleSubmit} noValidate>
                <KitField label="Nova senha" required help={newErr} error>
                    <KitInputIco rightButtons={eye(showNewPassword, setShowNewPassword)}>
                        <KitInput name="newPassword" type={showNewPassword ? 'text' : 'password'} placeholder="Mínimo 4 caracteres" error={!!newErr} value={formik.values.newPassword} onChange={formik.handleChange} onBlur={formik.handleBlur} sx={{ pr: '48px' }} />
                    </KitInputIco>
                </KitField>
                <KitProgress value={s.score * 25} tone={s.tone} sx={{ m: '-4px 0 6px' }} />
                <Box sx={{ fontSize: 12, color: 'text.disabled', m: '5px 2px 14px' }}>
                    {formik.values.newPassword ? <>Força: <b>{s.label}</b>{s.hint ? ` — ${s.hint}` : ''}</> : 'Digite a nova senha'}
                </Box>
                <KitField label="Confirmar nova senha" required help={confirmErr} error>
                    <KitInputIco rightButtons={eye(showConfirmPassword, setShowConfirmPassword)}>
                        <KitInput name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} placeholder="Repita a nova senha" error={!!confirmErr} value={formik.values.confirmPassword} onChange={formik.handleChange} onBlur={formik.handleBlur} sx={{ pr: '48px' }} />
                    </KitInputIco>
                </KitField>
            </Box>

            <KitFormBar>
                <KitButton type="submit" form="mobile-change-password" variant="primary" disabled={isLoading}>
                    {isLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar nova senha'}
                </KitButton>
            </KitFormBar>
        </MobileAuthPage>
    );
}
